import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { BadgeCheck, Check, Mail, MapPin, Phone, Send, Trash2, X } from "lucide-react";
import { Avatar } from "./widgets";
import { TaskForm, TaskRow } from "./taskWidgets";
import { addNote, currentUser, getLead, type TaskInput } from "./api";
import { formatCurrency, formatDateTime, productLabel, relativeTime } from "./format";
import {
  PRIORITIES,
  PRIORITY_META,
  STATUS_META,
  STATUS_ORDER,
  type AdminLead,
  type LeadNote,
  type LeadPriority,
  type LeadStatus,
  type LeadTask
} from "./types";

export function LeadDrawer({
  lead,
  leads,
  tasks,
  onClose,
  onUpdate,
  onDeleteLead,
  onCreateTask,
  onToggleTask,
  onRescheduleTask,
  onDeleteTask
}: {
  lead: AdminLead;
  leads: AdminLead[];
  tasks: LeadTask[];
  onClose: () => void;
  onUpdate: (
    id: string,
    patch: { status?: LeadStatus; owner?: string; priority?: LeadPriority }
  ) => Promise<void>;
  onDeleteLead: (id: string) => Promise<void>;
  onCreateTask: (input: TaskInput) => Promise<void>;
  onToggleTask: (task: LeadTask, done: boolean) => void;
  onRescheduleTask: (task: LeadTask) => void;
  onDeleteTask: (task: LeadTask) => void;
}) {
  const [notes, setNotes] = useState<LeadNote[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(true);
  const [ownerDraft, setOwnerDraft] = useState(lead.owner);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const leadTasks = useMemo(
    () =>
      tasks
        .filter((task) => task.leadId === lead.id)
        .sort((a, b) => Number(a.done) - Number(b.done) || a.dueAt.localeCompare(b.dueAt)),
    [tasks, lead.id]
  );

  const removeLead = async () => {
    setDeleting(true);
    try {
      await onDeleteLead(lead.id);
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    setOwnerDraft(lead.owner);
    setConfirmDelete(false);
  }, [lead.owner, lead.id]);

  useEffect(() => {
    let active = true;
    setLoadingNotes(true);
    getLead(lead.id)
      .then((res) => {
        if (active) setNotes(res.notes);
      })
      .catch(() => {
        if (active) setNotes([]);
      })
      .finally(() => {
        if (active) setLoadingNotes(false);
      });
    return () => {
      active = false;
    };
  }, [lead.id]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const changeStatus = async (status: LeadStatus) => {
    if (status === lead.status || busy) return;
    setBusy(true);
    try {
      await onUpdate(lead.id, { status });
    } finally {
      setBusy(false);
    }
  };

  const changePriority = async (priority: LeadPriority) => {
    if (priority === lead.priority) return;
    setBusy(true);
    try {
      await onUpdate(lead.id, { priority });
    } finally {
      setBusy(false);
    }
  };

  const saveOwner = async () => {
    const next = ownerDraft.trim();
    if (next === lead.owner) return;
    setBusy(true);
    try {
      await onUpdate(lead.id, { owner: next });
    } finally {
      setBusy(false);
    }
  };

  const submitNote = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const body = noteDraft.trim();
    if (!body) return;
    setSavingNote(true);
    try {
      const note = await addNote(lead.id, body, currentUser() ?? "");
      setNotes((prev) => [note, ...prev]);
      setNoteDraft("");
    } catch {
      /* zignoruj — pozostaw treść do ponowienia */
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <>
      <div className="crm-drawer-overlay" onClick={onClose} />
      <aside className="crm-drawer" role="dialog" aria-modal="true" aria-label={`Zgłoszenie: ${lead.name}`}>
        <header className="crm-drawer-head">
          <Avatar name={lead.name} size={46} />
          <div className="crm-drawer-title">
            <strong>{lead.name}</strong>
            <span>
              <MapPin size={13} aria-hidden="true" /> {lead.city || "—"} · {relativeTime(lead.createdAt)}
            </span>
          </div>
          <div className="crm-drawer-head-actions">
            <button
              type="button"
              className="crm-icon-btn danger"
              onClick={() => setConfirmDelete(true)}
              title="Usuń lead"
              aria-label="Usuń lead"
            >
              <Trash2 size={17} aria-hidden="true" />
            </button>
            <button type="button" className="crm-icon-btn" onClick={onClose} aria-label="Zamknij">
              <X size={18} aria-hidden="true" />
            </button>
          </div>
        </header>

        {confirmDelete && (
          <div className="crm-confirm" role="alertdialog">
            <p>
              Usunąć lead <strong>{lead.name}</strong> wraz z notatkami i zadaniami? Tej operacji nie można cofnąć.
            </p>
            <div className="crm-confirm-actions">
              <button
                type="button"
                className="crm-btn crm-btn--ghost"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
              >
                Anuluj
              </button>
              <button type="button" className="crm-btn crm-btn--danger" onClick={removeLead} disabled={deleting}>
                <Trash2 size={15} aria-hidden="true" /> {deleting ? "Usuwanie…" : "Usuń lead"}
              </button>
            </div>
          </div>
        )}

        <div className="crm-drawer-actions">
          <a className="crm-btn crm-btn--primary" href={`tel:${lead.phone}`}>
            <Phone size={16} aria-hidden="true" /> Zadzwoń
          </a>
          {lead.email && (
            <a className="crm-btn crm-btn--ghost" href={`mailto:${lead.email}`}>
              <Mail size={16} aria-hidden="true" /> E-mail
            </a>
          )}
        </div>

        <div className="crm-drawer-body">
          <section className="crm-drawer-section">
            <h4>Etap w lejku</h4>
            <div className="crm-status-track">
              {STATUS_ORDER.map((status) => (
                <button
                  key={status}
                  type="button"
                  className={`crm-status-step ${STATUS_META[status].tone}${lead.status === status ? " is-active" : ""}`}
                  onClick={() => changeStatus(status)}
                  disabled={busy}
                >
                  {STATUS_META[status].label}
                </button>
              ))}
            </div>
          </section>

          <section className="crm-drawer-section crm-drawer-grid">
            <label className="crm-field">
              <span>Opiekun</span>
              <input
                value={ownerDraft}
                placeholder="Nieprzypisany"
                onChange={(event) => setOwnerDraft(event.target.value)}
                onBlur={saveOwner}
                onKeyDown={(event) => {
                  if (event.key === "Enter") (event.target as HTMLInputElement).blur();
                }}
              />
            </label>
            <label className="crm-field">
              <span>Priorytet</span>
              <select
                className="crm-select"
                value={lead.priority}
                onChange={(event) => changePriority(event.target.value as LeadPriority)}
              >
                {PRIORITIES.map((priority) => (
                  <option key={priority} value={priority}>
                    {PRIORITY_META[priority].label}
                  </option>
                ))}
              </select>
            </label>
          </section>

          <section className="crm-drawer-section">
            <h4>Zadania i przypomnienia</h4>
            {leadTasks.length === 0 ? (
              <p className="crm-empty">Brak zadań dla tego leada. Dodaj przypomnienie poniżej.</p>
            ) : (
              <div className="crm-task-list">
                {leadTasks.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    showLead={false}
                    onToggle={onToggleTask}
                    onReschedule={onRescheduleTask}
                    onDelete={onDeleteTask}
                  />
                ))}
              </div>
            )}
            <TaskForm leads={leads} defaultLeadId={lead.id} lockLead onSubmit={onCreateTask} />
          </section>

          <section className="crm-drawer-section">
            <h4>Szczegóły zgłoszenia</h4>
            <dl className="crm-detail-list">
              <Detail label="Produkt" value={productLabel(lead.product)} />
              <Detail label="Telefon" value={<a href={`tel:${lead.phone}`}>{lead.phone}</a>} />
              <Detail label="E-mail" value={lead.email ? <a href={`mailto:${lead.email}`}>{lead.email}</a> : "—"} />
              <Detail label="Pora kontaktu" value={lead.contactTime || "dowolnie"} />
              <Detail label="Budżet" value={lead.budget != null ? `${formatCurrency(lead.budget)} / mies.` : "—"} />
              <Detail label="Wiek" value={lead.age || "—"} />
              <Detail label="Sytuacja" value={lead.family || "—"} />
              <Detail label="Źródło" value={lead.source || "—"} />
              <Detail label="Utworzono" value={formatDateTime(lead.createdAt)} />
            </dl>

            {lead.selectedNeeds.length > 0 && (
              <div className="crm-chips">
                {lead.selectedNeeds.map((need, idx) => (
                  <span key={idx} className="crm-chip">
                    {need}
                  </span>
                ))}
              </div>
            )}

            {lead.recommendation && (
              <p className="crm-reco">
                <BadgeCheck size={15} aria-hidden="true" /> {lead.recommendation}
              </p>
            )}

            {lead.message && <blockquote className="crm-message">{lead.message}</blockquote>}
          </section>

          <section className="crm-drawer-section">
            <h4>Zgody (RODO)</h4>
            <ul className="crm-consents">
              <Consent ok={lead.contactConsent} label="Zgoda na kontakt" />
              <Consent ok={lead.privacyConsent} label="Informacja o przetwarzaniu danych" />
              <Consent ok={lead.marketingConsent} label="Zgoda marketingowa" />
            </ul>
            {lead.legalVersion && <p className="crm-legal-version">Wersja klauzuli: {lead.legalVersion}</p>}
          </section>

          <section className="crm-drawer-section">
            <h4>Notatki i historia</h4>
            <form className="crm-note-form" onSubmit={submitNote}>
              <textarea
                value={noteDraft}
                placeholder="Dodaj notatkę z rozmowy…"
                rows={2}
                onChange={(event) => setNoteDraft(event.target.value)}
              />
              <button className="crm-btn crm-btn--primary" type="submit" disabled={savingNote || !noteDraft.trim()}>
                <Send size={15} aria-hidden="true" /> {savingNote ? "Zapis…" : "Dodaj notatkę"}
              </button>
            </form>
            <ul className="crm-timeline">
              {loadingNotes && <li className="crm-timeline-empty">Ładowanie…</li>}
              {!loadingNotes && notes.length === 0 && (
                <li className="crm-timeline-empty">Brak notatek. Dodaj pierwszą po rozmowie.</li>
              )}
              {notes.map((note) => (
                <li key={note.id}>
                  <span className="crm-timeline-dot" />
                  <div>
                    <div className="crm-timeline-meta">
                      <strong>{note.author || "System"}</strong>
                      <span>{relativeTime(note.createdAt)}</span>
                    </div>
                    <p>{note.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </aside>
    </>
  );
}

function Detail({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="crm-detail">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function Consent({ ok, label }: { ok: boolean; label: string }) {
  return (
    <li className={ok ? "is-ok" : "is-no"}>
      {ok ? <Check size={15} aria-hidden="true" /> : <X size={15} aria-hidden="true" />}
      <span>{label}</span>
    </li>
  );
}
